import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import PriceChart from "../components/PriceChart";
import NewsCard from "../components/NewsCard";
import { ArrowLeft, TrendingUp, TrendingDown, Globe, ArrowLeftRight } from "lucide-react";

const STOCK_BIOS = {
  AAPL: "Apple Inc. designs, manufactures, and markets consumer electronics, software, and services, including the iPhone, Mac, iPad, and Apple Watch. Headquartered in Cupertino, California, Apple is one of the largest companies in the world by market capitalization.",
  MSFT: "Microsoft Corporation develops, licenses, and sells computer software, consumer electronics, and personal computers. Its products include Windows, Office, Azure cloud services, and the Xbox gaming platform.",
  GOOGL: "Alphabet Inc. is the parent company of Google, providing search, advertising, cloud computing, and consumer technology products. Its portfolio also includes YouTube, Android, and Waymo self-driving technology.",
  AMZN: "Amazon.com Inc. is a global e-commerce and cloud computing company. Beyond online retail, Amazon Web Services (AWS) is one of the world's leading cloud infrastructure providers.",
  NVDA: "NVIDIA Corporation designs graphics processing units (GPUs) for gaming, professional visualization, and data center markets. Its chips power much of the infrastructure behind modern AI training and inference.",
  META: "Meta Platforms Inc. operates Facebook, Instagram, WhatsApp, and Messenger, and develops virtual and augmented reality hardware through its Reality Labs division.",
  TSLA: "Tesla Inc. designs and manufactures electric vehicles, battery energy storage, and solar products. The company is also developing autonomous driving technology and robotics.",
  AVGO: "Broadcom Inc. designs and develops semiconductor and infrastructure software solutions, with products spanning networking, broadband, wireless, and enterprise storage.",
  "BRK.B": "Berkshire Hathaway Inc. is a multinational holding company led by Warren Buffett, with major holdings across insurance, railroads, energy, and public equities.",
  JPM: "JPMorgan Chase & Co. is one of the largest banking institutions in the United States, offering investment banking, consumer banking, and asset management services globally.",
  V: "Visa Inc. operates the world's largest retail electronic payments network, facilitating transactions between merchants, banks, and consumers across more than 200 countries.",
  MA: "Mastercard Inc. is a global payments technology company connecting consumers, financial institutions, and merchants in more than 210 countries and territories.",
  COIN: "Coinbase Global Inc. operates a cryptocurrency exchange platform, providing trading, custody, and infrastructure services for retail and institutional crypto investors.",
  MSTR: "MicroStrategy Inc. (now Strategy) is a business intelligence company that has become widely known for holding one of the largest corporate Bitcoin treasuries in the world.",
  NFLX: "Netflix Inc. is a subscription streaming service offering movies, TV series, and original content to members in over 190 countries.",
  AMD: "Advanced Micro Devices Inc. designs high-performance computing and graphics processors, competing directly with Intel and NVIDIA across CPU and GPU markets.",
  DIS: "The Walt Disney Company is a global entertainment conglomerate spanning film studios, theme parks, and streaming services including Disney+.",
  PYPL: "PayPal Holdings Inc. operates a digital payments platform enabling online money transfers, serving as an alternative to traditional paper methods for merchants and consumers.",
  INTC: "Intel Corporation designs and manufactures semiconductor chips, historically dominant in PC and server processors, and expanding into foundry manufacturing services.",
  BA: "The Boeing Company designs, manufactures, and sells commercial jetliners, defense products, and space systems, and is one of the world's largest aerospace companies.",
  SHOP: "Shopify Inc. provides a commerce platform that allows businesses of all sizes to set up online stores and sell across web, mobile, social media, and physical retail locations.",
  UBER: "Uber Technologies Inc. operates a global platform for ride-hailing, food delivery, and freight logistics, connecting riders and drivers through its mobile app.",
  SNAP: "Snap Inc. develops Snapchat, a camera and messaging app popular with younger audiences, along with augmented reality lenses and advertising products.",
  PLTR: "Palantir Technologies Inc. builds data analytics software used by government agencies and large enterprises to integrate and analyze complex datasets.",
  NKE: "Nike Inc. designs, develops, and sells athletic footwear, apparel, and equipment, and is one of the world's largest and most recognized sportswear brands.",
  CRM: "Salesforce Inc. is a cloud-based customer relationship management (CRM) company, helping businesses manage sales, marketing, and customer service.",
  ORCL: "Oracle Corporation develops database software, cloud infrastructure, and enterprise applications used by businesses and governments worldwide.",
  ADBE: "Adobe Inc. creates software for content creation and digital marketing, including Photoshop, Premiere Pro, and the Creative Cloud subscription suite.",
  QCOM: "Qualcomm Inc. designs semiconductors and wireless technology, and its chips power a large share of the world's smartphones and mobile devices.",
  SBUX: "Starbucks Corporation operates one of the world's largest coffeehouse chains, roasting and selling coffee through company-owned and licensed retail locations globally.",
};

function Converter({ price, symbol }) {
  const [shares, setShares] = useState("1");
  const usd = (parseFloat(shares) || 0) * (price || 0);
  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5">
      <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">{symbol} Converter</h3>
      <input type="number" value={shares} onChange={(e) => setShares(e.target.value)}
        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 mb-2" />
      <div className="flex justify-center py-1"><ArrowLeftRight className="w-4 h-4 text-[var(--text-tertiary)]" /></div>
      <div className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-lg px-3 py-2.5 text-sm text-[var(--text-primary)] flex items-center justify-between">
        <span>${usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
        <span className="text-xs font-mono text-[var(--text-tertiary)]">USD</span>
      </div>
    </div>
  );
}

export default function StockDetail() {
  const { symbol } = useParams();
  const [stock, setStock] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      api.get("/stocks/" + symbol),
      api.get("/news?limit=50").catch(() => ({ data: [] })),
    ])
      .then(([s, n]) => {
        setStock(s.data);
        const keyword = s.data.name.split(" ")[0].toLowerCase();
        const matches = (n.data || []).filter((a) =>
          a.title?.toLowerCase().includes(symbol.toLowerCase()) ||
          a.title?.toLowerCase().includes(keyword)
        ).slice(0, 3);
        setNews(matches);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Loading...</div>;
  if (error || !stock) return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Stock not found.</div>;

  const isUp = stock.change_pct >= 0;

  return (
    <div className="pt-10 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/stocks" className="inline-flex items-center gap-2 text-sm font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Stocks
        </Link>

        <div className="flex items-center gap-4 mb-8">
          {stock.logo ? (
            <img src={stock.logo} alt={stock.symbol} className="w-14 h-14 rounded-full object-contain bg-white p-1.5" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
              <span className="text-yellow-400 font-bold text-lg">{stock.symbol[0]}</span>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display font-bold text-3xl text-[var(--text-primary)]">{stock.name}</h1>
              <span className="text-xs font-mono px-2 py-0.5 border border-[var(--border-soft)] text-[var(--text-tertiary)] rounded">{stock.symbol}</span>
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-display font-bold text-2xl text-[var(--text-primary)]">${stock.price?.toFixed(2)}</span>
              <span className={"flex items-center gap-1 font-mono text-sm font-bold " + (isUp ? "text-green-400" : "text-red-400")}>
                {isUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                {isUp ? "+" : ""}{stock.change_pct?.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6">
              <h2 className="font-display font-bold text-lg text-[var(--text-primary)] mb-4">{stock.name} Price Chart</h2>
              <PriceChart endpoint={`/stocks/${stock.symbol}/history`} />
            </div>

            {STOCK_BIOS[stock.symbol] && (
              <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6">
                <h2 className="font-display font-bold text-lg text-[var(--text-primary)] mb-3">About {stock.name}</h2>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{STOCK_BIOS[stock.symbol]}</p>
              </div>
            )}

            {news.length > 0 && (
              <div>
                <h2 className="font-display font-bold text-xl text-[var(--text-primary)] mb-4"><span className="text-yellow-400">/</span> Latest {stock.symbol} News</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {news.map((n) => <NewsCard key={n.id} article={n} />)}
                </div>
              </div>
            )}
          </div>

          {/* Right */}
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-5 space-y-3.5">
              {[
                ["Open", "$" + stock.open?.toFixed(2)],
                ["Day High", "$" + stock.high?.toFixed(2)],
                ["Day Low", "$" + stock.low?.toFixed(2)],
                ["Prev Close", "$" + stock.prev_close?.toFixed(2)],
                ["Exchange", stock.exchange || "—"],
                ...(stock.industry ? [["Industry", stock.industry]] : []),
                ...(stock.market_cap ? [["Market Cap", "$" + (stock.market_cap / 1000).toFixed(1) + "B"]] : []),
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{label}</span>
                  <span className="font-bold text-sm text-[var(--text-primary)]">{value}</span>
                </div>
              ))}
              {stock.website && (
                <a href={stock.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-yellow-400 hover:underline pt-2">
                  <Globe className="w-4 h-4" /> Company Website
                </a>
              )}
            </div>

            <Converter price={stock.price} symbol={stock.symbol} />
          </div>
        </div>

        <p className="text-xs text-[var(--text-tertiary)] text-center mt-10">
          Data from Finnhub and Yahoo Finance. Prices may be delayed. Not financial advice.
        </p>
      </div>
    </div>
  );
}